import type { RoomType } from 'src/common/interfaces/room';
import type { RoomTask } from 'src/common/interfaces/tasks';

import { useAppSelector } from 'src/app/hooks';
import type { RootState } from 'src/app/store';
import { RoomType as RoomTypeEnum } from 'src/common/enums/rooms';
import { useGetUserQuery } from 'src/common/slices';

// Exclude AoC/advent rooms by code or name
const isAocOrAdvent = (str?: string) => !!str && /aoc|advent/i.test(str);

/**
 * Determines which tasks in a room should be locked based on user authentication, room type, and completion status.
 *
 * @param {boolean} isPublicRoom - Whether the room is public.
 * @param {RoomTask[]} [tasks] - The list of tasks in the room.
 * @param {RoomType} [roomType] - The type of the room (e.g., walkthrough, challenge).
 * @param {string} [roomCode] - The code of the room.
 * @param {string} [roomName] - The name of the room.
 * @param {boolean} [roomForceCompleteInOrder] - Room setting for task ordering.
 * @returns {RoomTask[]} An array of tasks that should be locked (i.e., not yet available to the user).
 */
export const useLockedTasks = (
  isPublicRoom: boolean,
  tasks?: RoomTask[],
  roomType?: RoomType,
  roomCode?: string,
  roomName?: string,
  roomForceCompleteInOrder?: boolean,
) => {
  const { isSuccess: isUserAuth } = useGetUserQuery();
  const { taskOrderOverride } = useAppSelector((state: RootState) => state.room);

  // Check for override first - if override is enabled and disableTaskOrdering is true, return no locked tasks
  if (taskOrderOverride.isOverrideEnabled && taskOrderOverride.disableTaskOrdering) {
    return [];
  }

  // Exclude if not authenticated or not public
  if (!isUserAuth || !isPublicRoom) {
    return [];
  }

  // Check room setting - defaults to true for non-challenges, false for challenges
  const shouldEnforceOrder = roomForceCompleteInOrder ?? roomType !== RoomTypeEnum.CHALLENGE;

  if (shouldEnforceOrder === false) {
    return [];
  }

  // Exclude challenge rooms
  if (roomType && roomType.toLowerCase() === RoomTypeEnum.CHALLENGE) {
    return [];
  }

  // Skip rooms with aoc/advent in code/name
  if (isAocOrAdvent(roomCode) || isAocOrAdvent(roomName)) {
    return [];
  }

  // For locked tasks, we need to check task completion status
  if (!tasks || tasks.length === 0) {
    return [];
  }

  const reverseSortedTasks = [...(tasks || [])]?.sort((task1, task2) => task2.taskNo - task1.taskNo);
  const highestCompletedTaskNo =
    reverseSortedTasks?.find((task) => task.questions.every((question) => !!question.progress?.correct))?.taskNo ?? 0;
  return reverseSortedTasks?.filter((task) => task.taskNo > highestCompletedTaskNo + 1);
};
